import { useMemo, useCallback } from "react";
import { PermissionBits } from "@/util/permission_bits";

// Define display labels for each permission name
const LABELS = {
  VIEW: "View",
  ADD: "Add",
  MODIFY: "Modify",
  DELETE: "Delete",
  FIND: "Find",
  LOCK: "Lock",
  UNLOCK: "Unlock",
  PRINT: "Print",
  REPORTS: "Reports",
  TABLEVIEW: "Table View",
  ALL: "All Rights",
  NONE: "No Rights",
};

export const LABELS_CAPITAL = {
  VIEW: "VIEW",
  ADD: "ADD",
  MODIFY: "MODIFY",
  DELETE: "DELETE",
  FIND: "FIND",
  LOCK: "LOCK",
  UNLOCK: "UNLOCK",
  PRINT: "PRINT",
  REPORTS: "REPORTS",
  TABLEVIEW: "TABLEVIEW",
  ALL: "ALL",
  NONE: "NONE",
};

// Define the default ordering
const ORDER = [
  "ADD",
  "ALL",
  "DELETE",
  "FIND",
  "LOCK",
  "MODIFY",
  "NONE",
  "PRINT",
  "REPORTS",
  "TABLEVIEW",
  "UNLOCK",
  "VIEW",
];

/**
 * Hook to build a static permission list and mask helpers.
 */
export function usePermissionMask() {
  // 1) map bits
  const nameToBit = useMemo(
    () =>
      Object.entries(PermissionBits).reduce((acc, [bit, name]) => {
        acc[name] = Number(bit);
        return acc;
      }, {}),
    []
  );

  // 2) build items list
  const items = useMemo(
    () =>
      ORDER.map((name) => ({
        constantName: name,
        defaultString: LABELS[name],
        bit: nameToBit[name] || 0,
      })),
    [nameToBit]
  );

  // 3) lookup table
  const lookup = useMemo(
    () =>
      items.reduce((m, i) => {
        m[i.constantName] = i.bit;
        return m;
      }, {}),
    [items]
  );

  // 4) memoized helpers
  const parsePermissionMask = useCallback(
    (mask = 0) => {
      const fullMask = lookup["ALL"] || 0;
      const retArr = items?.reduce((out, i) => {
        if (i.constantName === "ALL") {
          // only check “All Rights” when mask exactly matches fullMask
          // out[i.constantName] = mask == fullMask;
        } else {
          out[i.constantName] = Boolean(mask & i.bit);
        }
        return out;
      }, {});

      const none = Object.keys(retArr)?.every((r) => !retArr[r]);
      if (none) {
        retArr["NONE"] = true;
      }

      return retArr;
    },
    [items, lookup]
  );

  const buildPermissionMask = useCallback(
    (selection = {}) => {
      return Object.entries(selection).reduce(
        (sum, [cn, on]) => (on ? sum | (lookup[cn] || 0) : sum),
        0
      );
    },
    [lookup]
  );
  return { items, LABELS_CAPITAL, parsePermissionMask, buildPermissionMask };
}
